import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import {
  LaunchableIntentSelector,
  LaunchablePath,
} from "./launchable-intent-selector";
import { CodePathForm } from "./code-path-form";
import {
  UIBuilderContainer,
  ContainerConfiguration,
  generateDockerComposeYaml,
} from "./container-configuration";
import { JupyterConfiguration } from "./jupyter-configuration";
import { ReviewConfiguration } from "./review-configuration";
import { Button } from "components/Graphs/Button";
import { ArrowLeft, ArrowRight, Loader2, RotateCcw } from "lucide-react";
import ComputeSelector from "components/Environment/Create/ComputeSelector";
import { OrgContext } from "contexts/OrgContext";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import {
  allPublicCloudsName,
  hasVerbBaseImageContainer,
} from "components/Environment/Create/utils";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import LaunchableComputeSelect from "./compute-selector";
import LaunchableAnimation, {
  LaunchableAnimationRef,
} from "./connection-stepper";
import WireIcon from "assets/img/svg/wire.svg?react";
import { FilePiece } from "./LaunchablePieces/Files";
import { ContainerPiece } from "./LaunchablePieces/Container";
import { ExposedPortsPiece } from "./LaunchablePieces/ExposedPorts";
import { ComputePiece } from "./LaunchablePieces/Compute";
import NetworkAccessConfiguration, {
  FirewallRule,
  NetworkService,
} from "./network-access-configuration";
import QuickStartConfiguration from "./quick-start-configuration";
import ContainerModeSelector, {
  ContainerMode,
} from "./container-mode-selector";
import {
  createFileRequest,
  ProcessedLaunchableData,
  transformPortListToMap,
} from "../../utils";
import { MockDeployModal } from "./mock-deploy-model";
import {
  CreateLaunchableRequest,
  LaunchableBuildRequest,
  LaunchableCreateWorkspaceRequest,
  LaunchableFileRequest,
} from "server";
import { LaunchableExposePorts } from "..";
import { buildDefaultVerbYamlConfig } from "components/Verb/utils";
import agent from "server";
import {
  CONSOLE_1_AUTH0_DEPLOYMENT_URL,
  CONSOLE_1_KAS_DEPLOYMENT_URL,
  isKasAuthFlow,
} from "server/kas/utils";
import { UserContext } from "contexts/UserContext";
import { AnimatePresence, motion } from "framer-motion";

export type RuntimeType = "VM" | "container" | undefined;
type Step =
  | "intent"
  | "quick-start"
  | "source"
  | "container-mode"
  | "network"
  | "compute"
  | "review";

export const LaunchableCreator: React.FC = () => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState<Step>("source");
  const [selectedPath, setSelectedPath] = useState<LaunchablePath>("custom");
  const [runtimeType, setRuntimeType] = useState<RuntimeType>();
  const [codeSource, setCodeSource] = useState<string>("");
  const [codeSourceType, setCodeSourceType] = useState<
    "url/upload" | "embedded" | "none" | ""
  >("");
  const [codeSourceError, setCodeSourceError] = useState<string | null>(null);
  //container mode
  const [containerMode, setContainerMode] = useState<ContainerMode>("featured");
  const [useExistingCompose, setUseExistingCompose] = useState<boolean>(false);
  const [selectedContainer, setSelectedContainer] =
    useState<ContainerSelected>();
  const [pythonVersion, setPythonVersion] = useState<string>("3.10");
  const [cudaVersion, setCudaVersion] = useState<string>("12.0.1");
  const [UIBuilderContainers, setUIBuilderContainers] = useState<
    UIBuilderContainer[]
  >([]);

  // Network and Jupyter configuration
  const [jupyterDirectory, setJupyterDirectory] =
    useState<string>("/workspace");
  const [jupyterStatus, setJupyterStatus] = useState<
    "configured" | "not-configured"
  >("configured");
  const [services, setServices] = useState<NetworkService[]>([]);
  // Add firewall rules state
  const [firewallRules, setFirewallRules] = useState<FirewallRule[]>([]);

  //Compute
  const [selectedInstanceType, setSelectedInstanceType] =
    useState<GPUInstanceType>();
  const [storage, setStorage] = useState<string>("256");

  //Review
  const [name, setName] = useState<string>("");
  const [launchableURL, setLaunchableURL] = useState<string>("");
  const [errorCreatingLaunchable, setErrorCreatingLaunchable] =
    useState<string>("");
  const [loadingCreatingLaunchable, setLoadingCreatingLaunchable] =
    useState<boolean>(false);

  // Mock Deploy Modal
  const [showMockDeployModal, setShowMockDeployModal] =
    useState<boolean>(false);
  const [mockLaunchableData, setMockLaunchableData] =
    useState<ProcessedLaunchableData>();

  const [currentNumberPiece, setCurrentNumberPiece] = useState<number>(0);

  const launchableRef = useRef<LaunchableAnimationRef>(null);
  const pieces = [
    { id: 1, position: 0, content: <div>Compute</div> },
    { id: 2, position: 1, content: <div>Container</div> },
    { id: 3, position: 2, content: <div>Expose Ports</div> },
    { id: 4, position: 3, content: <div>Files</div> },
  ];

  const addLaunchablePiece = (id: number, content: React.ReactNode) => {
    launchableRef.current?.addPieceWithContent(id, content);
    setCurrentNumberPiece(currentNumberPiece + 1);
  };

  const resetLaunchablePieces = () => launchableRef.current?.resetLaunchable();
  const removeLaunchablePiece = (id: number) =>
    launchableRef.current?.removePiece(id);

  const handleCodeSourceTypeChange = (
    type: "url/upload" | "embedded" | "none"
  ) => {
    setCodeSourceType(type);
  };

  const handlePathSelect = (path: LaunchablePath) => {
    setSelectedPath(path);
    if (path === "quick-start") {
      // For quick start, we'll set up a simple VM environment with Jupyter
      setRuntimeType("VM");
      setJupyterStatus("configured");
      // Go to the quick-start step
      setCurrentStep("quick-start");
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: true,
        },
      });
      addLaunchablePiece(
        2,
        <ContainerPiece
          python={""}
          cuda={""}
          container={{
            vmBuild: {
              forceJupyterInstall: true,
            },
          }}
        />
      );
      setTimeout(() => {
        addLaunchablePiece(
          3,
          <ExposedPortsPiece
            ports={{
              jupyter: "8888",
            }}
            firewallRules={[]}
          />
        );
      });
    } else {
      // For custom, we'll show the full configuration flow
      setCurrentStep("source");
    }
  };

  const handleRuntimeSelect = (runtime: RuntimeType) => {
    setRuntimeType(runtime);
  };

  const handleCodeSourceChange = (source: string) => {
    setCodeSource(source);
  };

  const handleExistingComposeToggle = (value: boolean) => {
    setUseExistingCompose(value);
  };

  const handleJupyterStatusChange = (
    status: "configured" | "not-configured"
  ) => {
    setJupyterStatus(status);
    if (runtimeType === "VM") {
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: status === "configured",
        },
      });
      addLaunchablePiece(
        2,
        <ContainerPiece
          python={""}
          cuda={""}
          container={{
            vmBuild: {
              forceJupyterInstall: status === "configured",
            },
          }}
        />
      );
    } else if (containerMode === "compose") {
      setSelectedContainer({
        dockerCompose: {
          ...selectedContainer?.dockerCompose,
          jupyterInstall: status === "configured",
        },
      });
      addLaunchablePiece(
        2,
        <ContainerPiece
          python={""}
          cuda={""}
          container={{
            dockerCompose: {
              ...selectedContainer?.dockerCompose,
              jupyterInstall: status === "configured",
            },
          }}
        />
      );
    }
  };

  const handleCreateLaunchable = async () => {
    setLoadingCreatingLaunchable(true);
    const exposedPorts: LaunchableExposePorts = {
      tunnels: services.map((service) => ({
        id: service.id,
        name: service.name,
        port: Number(service.port),
      })),
      firewallRules: firewallRules.map((rule) => ({
        id: rule.id,
        port: rule.ports,
      })),
    };
    if (selectedInstanceType && selectedContainer) {
      const createLaunchableRequest = makeCreateLaunchableRequest(
        name,
        storage,
        selectedInstanceType,
        pythonVersion,
        cudaVersion,
        selectedContainer,
        codeSource,
        exposedPorts
      );
      const res = await createLaunchable(
        orgContext.activeOrgId,
        createLaunchableRequest
      );
      if (!res) {
        setErrorCreatingLaunchable(
          "Error creating launchable, contact support or try again"
        );
        setLoadingCreatingLaunchable(false);
        return;
      }
      const createdLaunchableURL = `${
        isKasAuthFlow
          ? CONSOLE_1_KAS_DEPLOYMENT_URL
          : CONSOLE_1_AUTH0_DEPLOYMENT_URL
      }/launchable/deploy?launchableID=${encodeURIComponent(res.id)}`;
      setLaunchableURL(createdLaunchableURL);
      const extraEventProperties = {
        launchableID: res.id,
        launchableInstanceType: selectedInstanceType?.type,
        workspaceGroupID: selectedInstanceType?.workspace_groups[0].id,
        launchableCreatedByUserID: userContext.me?.id,
        launchableCreatedByOrgID: orgContext.activeOrgId,
        launchableRawURL: createdLaunchableURL,
      };
      agent.Brevent.track({
        eventName: "Launchable Created",
        userId: userContext.me?.id,
        properties: {
          orgId: orgContext.activeOrgId,
          noteBookName: name,
          ...extraEventProperties,
        },
      });
      setLoadingCreatingLaunchable(false);
    } else {
      setErrorCreatingLaunchable(
        "Missing required fields, instance type or container"
      );
      setLoadingCreatingLaunchable(false);
    }
  };

  const handleShowMockDeployModal = (show: boolean) => {
    setShowMockDeployModal(show);
    setMockLaunchableData(
      transformLaunchableStates({
        selectedInstanceType: selectedInstanceType,
        storage: storage,
        selectedContainer: selectedContainer,
        pythonVersion: pythonVersion,
        cudaVersion: cudaVersion,
        codeSource: codeSource,
        services: services,
        firewallRules: firewallRules,
        name: name || "My Launchable",
      })
    );
  };

  const isContainerNextDisabled = () => {
    if (currentStep === "container-mode") {
      if (containerMode === "featured") {
        return !selectedContainer?.verbBuild?.containerUrl;
      } else if (containerMode === "compose") {
        if (useExistingCompose) {
          return !(
            selectedContainer?.dockerCompose?.yamlString ||
            selectedContainer?.dockerCompose?.fileUrl
          );
        } else {
          return (
            UIBuilderContainers.length === 0 ||
            !containerBuilderValid(UIBuilderContainers)
          );
        }
      }
    }
    return false;
  };

  const isNetworkNextDisabled = () => {
    if (
      services.some((service) => service.hasInvalidPortError) ||
      services.some((service) => service.hasPortRangeError) ||
      services.some((service) => service.hasPortConflict)
    ) {
      return true;
    }

    if (
      firewallRules.some((rule) => rule.hasInvalidPortError) ||
      firewallRules.some((rule) => rule.hasPortConflict)
    ) {
      return true;
    }
    const nonJupyterServices = services.filter((service) => !service.isJupyter);
    if (
      nonJupyterServices.length >= 1 &&
      nonJupyterServices.some(
        (service) => service.name === "" || service.port === ""
      )
    ) {
      return true;
    }

    return false;
  };

  const handleNameChange = (name: string) => {
    setName(name);
  };

  const containerBuilderValid = (UIBuilderContainers: UIBuilderContainer[]) => {
    for (const container of UIBuilderContainers) {
      if (container.image.trim() === "") {
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    switch (currentStep) {
      case "intent":
        // No Next button on intent
        // Configure in handlePathSelect()
        break;
      case "quick-start":
        setCurrentStep("compute");
        break;
      case "source":
        if (selectedPath === "custom" && runtimeType === "VM") {
          setSelectedContainer({
            vmBuild: {
              forceJupyterInstall: true,
            },
          });
          setTimeout(() => {
            addLaunchablePiece(
              2,
              <ContainerPiece
                python={""}
                cuda={""}
                container={{
                  vmBuild: {
                    forceJupyterInstall: true,
                  },
                }}
              />
            );
          });
          setCurrentStep("network");
        } else if (selectedPath === "custom") {
          setCurrentStep("container-mode");
        }
        if (codeSource) {
          addLaunchablePiece(
            4,
            <FilePiece file={codeSource} disabled={false} />
          );
        }
        break;
      case "container-mode":
        if (containerMode === "compose" && !useExistingCompose) {
          setSelectedContainer({
            dockerCompose: {
              ...selectedContainer?.dockerCompose,
              yamlString: generateDockerComposeYaml(UIBuilderContainers),
              jupyterInstall:
                selectedContainer?.dockerCompose?.jupyterInstall === undefined
                  ? true
                  : !!selectedContainer?.dockerCompose?.jupyterInstall,
            },
          });
          addLaunchablePiece(
            2,
            <ContainerPiece
              python={""}
              cuda={""}
              container={{
                dockerCompose: {
                  ...selectedContainer?.dockerCompose,
                  yamlString: generateDockerComposeYaml(UIBuilderContainers),
                  jupyterInstall:
                    selectedContainer?.dockerCompose?.jupyterInstall ===
                    undefined
                      ? true
                      : !!selectedContainer?.dockerCompose?.jupyterInstall,
                },
              }}
            />
          );
        } else if (!!selectedContainer?.verbBuild) {
          setServices([]);
          setFirewallRules([]);
          addLaunchablePiece(
            2,
            <ContainerPiece
              python={""}
              cuda={""}
              container={selectedContainer || {}}
            />
          );
        }
        setCurrentStep("network");
        break;
      case "network":
        setCurrentStep("compute");
        if (services.length > 0 || firewallRules.length > 0) {
          addLaunchablePiece(
            3,
            <ExposedPortsPiece
              ports={services.reduce((acc, service) => {
                acc[service.name] = service.port;
                return acc;
              }, {} as Record<string, string>)}
              firewallRules={firewallRules.map((rule) => rule.ports)}
            />
          );
        } else {
          removeLaunchablePiece(3);
        }
        break;
      case "compute":
        setCurrentStep("review");
        addLaunchablePiece(
          1,
          <ComputePiece
            instanceType={selectedInstanceType}
            diskStorage={storage}
          />
        );
        break;
      default:
        break;
    }
  };

  const resetLaunchable = () => {
    setCurrentNumberPiece(0);
    resetLaunchablePieces();
    setSelectedPath("custom");
    setRuntimeType(undefined);
    setCodeSource("");
    setCodeSourceType("");
    setContainerMode("featured");
    setUseExistingCompose(false);
    setStorage("");
    setName("");
    setSelectedContainer(undefined);
    setSelectedInstanceType(undefined);
    setPythonVersion("3.10");
    setCudaVersion("12.0.1");
    setUIBuilderContainers([]);
    setJupyterDirectory("/workspace");
    setJupyterStatus("configured");
    setServices([]);
    setFirewallRules([]);
    setErrorCreatingLaunchable("");
    setLaunchableURL("");
  };

  const handleReset = () => {
    resetLaunchable();
    setCurrentStep("source");
  };

  const handleBack = () => {
    switch (currentStep) {
      case "source":
        resetLaunchable();
        setCurrentStep("intent");
        break;
      case "quick-start":
        resetLaunchable();
        setCurrentStep("intent");
        break;
      case "container-mode":
        setCurrentStep("source");
        break;
      case "network":
        if (selectedPath === "custom" && runtimeType === "VM") {
          setCurrentStep("source");
        } else if (selectedPath === "custom") {
          setCurrentStep("container-mode");
        }
        break;
      case "compute":
        if (selectedPath === "quick-start") {
          setCurrentStep("quick-start");
        } else {
          setCurrentStep("network");
        }
        break;
      case "review":
        setCurrentStep("compute");
        break;
      default:
        break;
    }
  };

  const renderNextStepButton = () => {
    const status = loadingCreatingLaunchable
      ? "loading"
      : launchableURL
      ? "success"
      : "idle";

    if (currentStep === "review") {
      return (
        <div className="flex flex-col items-center">
          <AnimatePresence mode="wait">
            {status === "idle" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Button
                  onClick={handleCreateLaunchable}
                  className="transition-all duration-300 relative overflow-hidden group max-h-10"
                  disabled={!name.trim() || loadingCreatingLaunchable}
                >
                  <motion.span
                    className="inline-block"
                    initial={{ y: 0 }}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    Create Launchable
                  </motion.span>
                  <motion.span
                    className="absolute inset-0 bg-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={{ scale: 0 }}
                    whileHover={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </Button>
              </motion.div>
            )}

            {status === "loading" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Button disabled className="text-primary max-h-10">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "linear",
                    }}
                    className="mr-2"
                  >
                    <Loader2 className="w-4 h-4" />
                  </motion.span>
                  Creating...
                </Button>
              </motion.div>
            )}

            {status === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Button
                  onClick={handleReset}
                  variant="secondary"
                  className="gap-2 transition-all duration-300"
                >
                  <RotateCcw className="w-4 h-4" />
                  Create Another
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
          {errorCreatingLaunchable && (
            <div className="text-red-500 text-sm mt-2">
              {errorCreatingLaunchable}
            </div>
          )}
        </div>
      );
    } else if (currentStep !== "intent") {
      return (
        <Button
          className="ml-auto max-h-10"
          onClick={handleNext}
          disabled={
            (currentStep === "source" &&
              selectedPath === "quick-start" &&
              !runtimeType) ||
            (currentStep === "container-mode" && isContainerNextDisabled()) ||
            (currentStep === "network" && isNetworkNextDisabled()) ||
            (currentStep === "compute" && !selectedInstanceType)
          }
        >
          Next <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      );
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "intent":
        return <LaunchableIntentSelector onSelect={handlePathSelect} />;
      case "quick-start":
        return (
          <QuickStartConfiguration
            codeSource={codeSource}
            onCodeSourceChange={handleCodeSourceChange}
            jupyterDirectory={jupyterDirectory}
            onJupyterDirectoryChange={setJupyterDirectory}
          />
        );
      case "source":
        return selectedPath === "custom" ? (
          <CodePathForm
            codeSource={codeSource}
            onCodeSourceChange={handleCodeSourceChange}
            runtimeType={runtimeType}
            onRuntimeSelect={handleRuntimeSelect}
            codeSourceType={codeSourceType}
            setCodeSourceType={handleCodeSourceTypeChange}
            codeSourceError={codeSourceError}
            setCodeSourceError={setCodeSourceError}
          />
        ) : (
          <div>ERROR</div>
        );
      case "container-mode":
        return (
          <ContainerModeSelector
            selectedMode={containerMode}
            onModeSelect={setContainerMode}
            useExistingCompose={useExistingCompose}
            onExistingComposeToggle={handleExistingComposeToggle}
            selectedContainer={selectedContainer}
            setSelectedContainer={setSelectedContainer}
            pythonVersion={pythonVersion}
            setPythonVersion={setPythonVersion}
            cudaVersion={cudaVersion}
            setCudaVersion={setCudaVersion}
            UIBuilderContainers={UIBuilderContainers}
            setUIBuilderContainers={setUIBuilderContainers}
            onResetDockerCompose={() => {
              setSelectedContainer({});
              removeLaunchablePiece(2);
            }}
          />
        );
      case "network":
        return (
          <NetworkAccessConfiguration
            services={services}
            onServicesChange={setServices}
            jupyterDirectory={jupyterDirectory}
            onJupyterDirectoryChange={setJupyterDirectory}
            jupyterStatus={jupyterStatus}
            onJupyterStatusChange={handleJupyterStatusChange}
            runtimeType={runtimeType || "VM"}
            container={
              selectedContainer || {
                vmBuild: {
                  forceJupyterInstall: true,
                },
              }
            }
            firewallRules={firewallRules}
            onFirewallRulesChange={setFirewallRules}
          />
        );
      case "compute":
        return (
          <LaunchableComputeSelect
            selectedInstanceType={selectedInstanceType}
            setSelectedInstanceType={setSelectedInstanceType}
            setStorage={setStorage}
            storage={storage}
          />
        );
      case "review":
        return (
          <ReviewConfiguration
            path={selectedPath || "quick-start"}
            runtime={runtimeType || "VM"}
            codeSource={codeSource}
            container={
              selectedContainer || {
                vmBuild: {
                  forceJupyterInstall: true,
                },
              }
            }
            handleShowMockDeployModal={handleShowMockDeployModal}
            jupyterStatus={jupyterStatus}
            name={name}
            setName={handleNameChange}
            launchableURL={launchableURL}
            loading={loadingCreatingLaunchable}
            handleReset={handleReset}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 py-5">
      <div className="pb-6">
        <MockDeployModal
          show={showMockDeployModal}
          setShow={setShowMockDeployModal}
          launchableData={mockLaunchableData}
          instanceObject={selectedInstanceType || null}
          setInstanceObject={(instanceObject) =>
            setSelectedInstanceType(instanceObject || undefined)
          }
        />
        <div className="flex justify-between items-center w-full h-full">
          <LaunchableAnimation
            ref={launchableRef}
            pieces={pieces}
            connectorContent={
              <div className="">
                <WireIcon className="text-white" width={50} height={50} />
              </div>
            }
          />

          <div className="flex flex-col justify-center items-center h-full w-max-content">
            {currentStep !== "intent" && (
              <>
                <div className="text-sm text-secondary w-max">
                  Step{" "}
                  {selectedPath === "quick-start"
                    ? currentStep === "quick-start"
                      ? "1 of 3"
                      : currentStep === "compute"
                      ? "2 of 3"
                      : "3 of 3"
                    : currentStep === "source"
                    ? "1 of 5"
                    : currentStep === "container-mode"
                    ? "2 of 5"
                    : currentStep === "network"
                    ? "3 of 5"
                    : currentStep === "compute"
                    ? "4 of 5"
                    : "5 of 5"}
                </div>
                <div className="flex space-x-2 mt-3">
                  {selectedPath === "quick-start" ? (
                    <>
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "quick-start"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "compute"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "review"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                    </>
                  ) : (
                    <>
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "source"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "container-mode"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "network"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "compute"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                      <div
                        className={`h-2 w-2 rounded-full ${
                          currentStep === "review"
                            ? "bg-highlight"
                            : "bg-gray-300"
                        }`}
                      />
                    </>
                  )}
                </div>
                {currentNumberPiece > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      resetLaunchable();
                      setCurrentStep("source");
                    }}
                    className="mt-3 h-8"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <hr className="my-5 dark:border dark:border-zinc-800" />

        {renderStepContent()}

        <div className="flex justify-between mt-8">
          {!launchableURL &&
          currentStep !== "intent" &&
          currentStep !== "source" ? (
            <Button
              variant="secondary"
              onClick={handleBack}
              className="max-h-10"
              disabled={loadingCreatingLaunchable}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          ) : (
            <div></div>
          )}
          {renderNextStepButton()}
        </div>
      </div>
    </div>
  );
};

export const transformLaunchableStates = ({
  selectedInstanceType,
  storage,
  selectedContainer,
  pythonVersion,
  cudaVersion,
  codeSource,
  services,
  firewallRules,
  name = "My Launchable", // Default name if not provided
}: {
  selectedInstanceType?: GPUInstanceType;
  storage: string;
  selectedContainer?: ContainerSelected;
  pythonVersion: string;
  cudaVersion: string;
  codeSource: string;
  services: NetworkService[];
  firewallRules: FirewallRule[];
  name?: string;
}): ProcessedLaunchableData => {
  // Transform ports from services array to required format
  const ports: Record<string, string> = services.reduce((acc, service) => {
    acc[service.name] = service.port;
    return acc;
  }, {} as Record<string, string>);

  // Transform firewall rules array to string array
  const transformedFirewallRules = firewallRules.map((rule) => rule.ports);

  return {
    instanceType: selectedInstanceType?.type || null,
    workspaceGroupId: selectedInstanceType?.workspace_groups?.[0]?.id || null,
    containerSelected: selectedContainer || {
      verbBuild: {
        containerUrl: "Default",
      },
    },
    cuda: cudaVersion || null,
    python: pythonVersion || null,
    diskStorage: storage || null,
    file: codeSource || null,
    ports: Object.keys(ports).length > 0 ? ports : null,
    firewallRules:
      transformedFirewallRules.length > 0 ? transformedFirewallRules : null,
    userId: null, // These would need to be set elsewhere
    orgId: null, // These would need to be set elsewhere
    name: name || null,
    launchableId: null, // This would be set after creation
    url: "", // This would be set after creation
    couponCode: null,
  };
};

export const makeCreateLaunchableRequest = (
  name: string,
  storage: string,
  instance: GPUInstanceType,
  pythonVersion: string,
  cudaVersion: string,
  container: ContainerSelected,
  file: string,
  exposedPorts: LaunchableExposePorts
): CreateLaunchableRequest => {
  //Worksake Request
  const createWorkspaceRequest: LaunchableCreateWorkspaceRequest = {
    instanceType: instance.type,
    workspaceGroupId: instance.workspace_groups[0].id,
    storage: instance.elastic_root_volume ? storage : "",
    exposedPorts: exposedPorts.firewallRules.map((rule) =>
      rule.port.toString()
    ),
  };

  const buildRequest: LaunchableBuildRequest = {
    ports: exposedPorts.tunnels.map((tunnel) => ({
      name: tunnel.name,
      port: tunnel.port.toString(),
    })),
  };

  if (container.vmBuild) {
    // Build Request
    buildRequest.vmBuild = {
      forceJupyterInstall: !!container.vmBuild?.forceJupyterInstall,
    };
  }

  let jupyterPort = 8888;

  if (name.includes("blueprint") || name.includes("Blueprint")) {
    jupyterPort = 8887;
  }

  console.log("jupyterPort at the start", jupyterPort);

  if (!!container.verbBuild) {
    const customYaml = buildDefaultVerbYamlConfig(
      hasVerbBaseImageContainer(container)
        ? container.verbBuild?.containerUrl
        : "",
      transformPortListToMap(
        exposedPorts.tunnels.map((tunnel) => ({
          name: tunnel.name,
          port: tunnel.port.toString(),
        }))
      ),
      cudaVersion,
      pythonVersion,
      jupyterPort,
      true
    );
    buildRequest.verbBuild = {
      verbYaml: customYaml,
    };
  }

  if (!!container.customContainer) {
    buildRequest.containerBuild = {
      containerUrl: container.customContainer.containerUrl,
      entryPoint: container.customContainer.entryPoint,
    };
  }

  if (!!container.dockerCompose) {
    buildRequest.dockerCompose = {
      fileUrl: container.dockerCompose.fileUrl,
      jupyterInstall: container.dockerCompose.jupyterInstall,
      yamlString: container.dockerCompose.fileUrl
        ? undefined
        : container.dockerCompose.yamlString,
      registries: container.dockerCompose.registries,
    };
  }

  // File Request
  let launchableFile: LaunchableFileRequest | null = null;
  const fileRequest = createFileRequest(file, container);
  if (fileRequest) {
    launchableFile = fileRequest[0];
  }

  return {
    name,
    createWorkspaceRequest: createWorkspaceRequest,
    buildRequest: buildRequest,
    file: launchableFile,
  };
};

export const createLaunchable = async (
  orgId: string,
  createLaunchableRequest: CreateLaunchableRequest
) => {
  const res = await agent.Launchables.createEnvironmentLaunchable(
    orgId,
    createLaunchableRequest
  );
  if (res.success && res.data) {
    return res.data;
  } else {
    return null;
  }
};
